import React from 'react';
import PropTypes from 'prop-types';
import RecipientSelect from 'components/common/select/RecipientSelect';
import { INPUT_CLS } from 'helpers/formStyles';

// One option a reminder declares (backend Reminders::Base.field), drawn from its type:
// integer → number box, multi → checkboxes, users → the shared recipient picker.

const FieldShell = ({ id, label, hint, children }) => (
  <div className="tw-min-w-0">
    <label htmlFor={id} className="tw-block tw-text-[13px] tw-font-medium tw-text-slate-700 tw-mb-1">{label}</label>
    {children}
    {hint && <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-text-slate-500">{hint}</p>}
  </div>
);
FieldShell.propTypes = { id: PropTypes.string, label: PropTypes.string.isRequired, hint: PropTypes.string, children: PropTypes.node };

const toggle = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

const ReminderFieldInput = ({ field, value, onChange, knownUsers }) => {
  const id = `reminder-field-${field.key}`;

  if (field.type === 'integer') {
    return (
      <FieldShell id={id} label={field.label} hint={field.hint}>
        <input id={id} type="number" min={field.min ?? undefined} max={field.max ?? undefined}
          className={`${INPUT_CLS} tw-w-32`} value={value}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
      </FieldShell>
    );
  }

  if (field.type === 'multi') {
    return (
      <fieldset className="tw-m-0 tw-p-0 tw-border-0 tw-min-w-0">
        <legend className="tw-p-0 tw-text-[13px] tw-font-medium tw-text-slate-700 tw-mb-1.5">{field.label}</legend>
        <div className="tw-flex tw-flex-col tw-gap-2">
          {field.choices.map((choice) => (
            <label key={choice.value} className="tw-flex tw-items-start tw-gap-2 tw-text-[13px] tw-text-slate-800 tw-cursor-pointer">
              <input type="checkbox" className="tw-mt-0.5" checked={value.includes(choice.value)}
                onChange={() => onChange(toggle(value, choice.value))} />
              <span>
                <span className="tw-font-semibold">{choice.label}</span>
                {choice.hint && <span className="tw-block tw-text-[12px] tw-text-slate-500">{choice.hint}</span>}
              </span>
            </label>
          ))}
        </div>
        {field.hint && <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-text-slate-500">{field.hint}</p>}
      </fieldset>
    );
  }

  // users — stored as active Portal users; the server rejects an address that is not one.
  return (
    <FieldShell label={field.label} hint={field.hint}>
      <RecipientSelect value={value} onChange={onChange} placeholder="Add people…" knownOptions={knownUsers} />
    </FieldShell>
  );
};

ReminderFieldInput.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['integer', 'multi', 'users']).isRequired,
    label: PropTypes.string.isRequired,
    hint: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    choices: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string, hint: PropTypes.string })),
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]).isRequired,
  onChange: PropTypes.func.isRequired,
  knownUsers: PropTypes.arrayOf(PropTypes.object),
};

export default ReminderFieldInput;
